'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { MessageSquare, Send, AlertCircle, CheckCircle, User, Reply, ChevronDown, ChevronUp } from 'lucide-react';
import { trpc } from '@/lib/trpc/client';
import { useUser, SignInButton, SignUpButton } from '@clerk/nextjs';
import { useTranslation } from 'react-i18next';

interface TravelGuideCommentsProps {
  guideId: number;
  locale: string;
}

interface CommentReply {
  id: number;
  comment: string;
  authorName: string;
  authorAvatarUrl: string | null;
  isTeamReply: boolean;
  editedAt: Date | null;
  createdAt: Date;
}

interface Comment {
  id: number;
  comment: string;
  authorName: string;
  authorAvatarUrl: string | null;
  isTeamReply: boolean;
  editedAt: Date | null;
  createdAt: Date;
  replies: CommentReply[];
}

function formatDateTime(date: Date, locale: string): string {
  const localeMap: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    uk: 'uk-UA',
  };

  const localeString = localeMap[locale] || 'en-US';
  const dateObj = new Date(date);

  const dateStr = dateObj.toLocaleDateString(localeString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const timeStr = dateObj.toLocaleTimeString(localeString, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${dateStr} at ${timeStr}`;
}

// Avatar component with fallback
function CommentAvatar({ avatarUrl, name, isTeamReply }: { avatarUrl: string | null; name: string; isTeamReply: boolean }) {
  if (isTeamReply) {
    // Team replies get the Revoyager compass icon
    return (
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center">
        <span className="text-white font-bold text-sm">R</span>
      </div>
    );
  }

  if (avatarUrl) {
    return (
      <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden">
        <Image
          src={avatarUrl}
          alt={name}
          width={40}
          height={40}
          className="rounded-full object-cover"
        />
      </div>
    );
  }

  // Fallback to initials or User icon
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
      {initials ? (
        <span className="text-primary-700 font-medium text-sm">{initials}</span>
      ) : (
        <User className="h-5 w-5 text-primary-600" />
      )}
    </div>
  );
}

// Single comment display component
function CommentItem({
  comment,
  locale,
  onReply,
  isReply = false,
  isReplying = false,
  onCancelReply,
  replyForm,
}: {
  comment: Comment | CommentReply;
  locale: string;
  onReply?: (commentId: number, authorName: string) => void;
  isReply?: boolean;
  isReplying?: boolean;
  onCancelReply?: () => void;
  replyForm?: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className={`${isReply ? 'ml-12 mt-4' : ''}`}>
      <div className="flex items-start gap-3">
        <CommentAvatar
          avatarUrl={comment.authorAvatarUrl}
          name={comment.authorName}
          isTeamReply={comment.isTeamReply}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className="font-medium text-gray-900">{comment.authorName}</span>
            {comment.isTeamReply && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {t('travelGuides.comments.teamBadge')}
              </span>
            )}
            <span className="text-sm text-gray-500">
              {formatDateTime(comment.createdAt, locale)}
            </span>
            {comment.editedAt && (
              <span className="text-xs text-gray-400 italic">
                ({t('travelGuides.comments.edited')})
              </span>
            )}
          </div>
          <p className="text-gray-700 whitespace-pre-wrap break-words">{comment.comment}</p>
          {!isReply && onReply && !isReplying && (
            <button
              onClick={() => onReply(comment.id, comment.authorName)}
              className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              <Reply className="h-4 w-4" />
              {t('travelGuides.comments.replyButton')}
            </button>
          )}
        </div>
      </div>

      {/* Inline reply form */}
      {isReplying && replyForm && (
        <div className="ml-12 mt-4 p-4 bg-primary-50 rounded-lg border-l-4 border-primary-500 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 mb-3">
            <Reply className="h-4 w-4 text-primary-600" />
            <span className="text-sm font-medium text-primary-700">
              {t('travelGuides.comments.replyingTo', { name: comment.authorName })}
            </span>
            <button
              type="button"
              onClick={onCancelReply}
              className="ml-auto text-sm text-primary-600 hover:text-primary-700 underline"
            >
              {t('travelGuides.comments.cancelReply')}
            </button>
          </div>
          {replyForm}
        </div>
      )}
    </div>
  );
}

export default function TravelGuideComments({ guideId, locale }: TravelGuideCommentsProps) {
  const { isSignedIn, user } = useUser();
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  // Form state for new top-level comments
  const [newComment, setNewComment] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [notifyOnReply, setNotifyOnReply] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);

  // Reply state - separate from new comment
  const [replyingTo, setReplyingTo] = useState<{ id: number; name: string } | null>(null);
  const [replyText, setReplyText] = useState('');

  // Expanded replies state
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set());

  // Fetch approved comments (public)
  const { data: commentsData, isLoading: isLoadingComments } = trpc.travelGuides.getComments.useQuery(
    { guideId, limit: 50 },
    { refetchOnWindowFocus: false }
  );

  // Add comment mutation
  const addComment = trpc.travelGuides.addComment.useMutation({
    onSuccess: () => {
      setNewComment('');
      setDisplayName('');
      setShowNameInput(false);
      utils.travelGuides.getComments.invalidate({ guideId });
    },
  });

  // Add reply mutation (separate to handle inline form)
  const addReply = trpc.travelGuides.addComment.useMutation({
    onSuccess: () => {
      setReplyText('');
      setReplyingTo(null);
      // Auto-expand replies for the parent comment
      if (replyingTo) {
        setExpandedReplies(prev => new Set([...prev, replyingTo.id]));
      }
      utils.travelGuides.getComments.invalidate({ guideId });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    addComment.mutate({
      guideId,
      comment: newComment.trim(),
      displayName: displayName.trim() || undefined,
      notifyOnReply,
    });
  };

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !replyingTo) return;

    addReply.mutate({
      guideId,
      comment: replyText.trim(),
      parentId: replyingTo.id,
      displayName: displayName.trim() || undefined,
      notifyOnReply,
    });
  };

  const handleReply = (commentId: number, authorName: string) => {
    setReplyingTo({ id: commentId, name: authorName });
    setReplyText('');
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const toggleReplies = (commentId: number) => {
    setExpandedReplies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const comments = (commentsData?.comments || []) as Comment[];

  // Get default display name from user
  const defaultName = user?.fullName || user?.firstName || '';

  return (
    <section id="comments" className="mt-12 pt-8 border-t border-gray-200">
      <div className="flex items-center gap-3 mb-2">
        <MessageSquare className="h-6 w-6 text-primary-600" />
        <h2 className="text-2xl font-serif text-primary-900">{t('travelGuides.comments.title')}</h2>
      </div>
      <p className="text-gray-600 mb-8">{t('travelGuides.comments.subtitle')}</p>

      {/* Comment Form - for new top-level comments only */}
      <div id="comment-form" className="mb-8">
        {isSignedIn ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                {user?.imageUrl ? (
                  <Image
                    src={user.imageUrl}
                    alt=""
                    width={40}
                    height={40}
                    className="rounded-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 text-primary-600" />
                )}
              </div>
              <div className="flex-1">
                {/* Name customization toggle */}
                <div className="mb-3">
                  <button
                    type="button"
                    onClick={() => setShowNameInput(!showNameInput)}
                    className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                  >
                    {t('travelGuides.comments.postingAs')}: <span className="font-medium">{displayName || defaultName || t('travelGuides.comments.anonymousUser')}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showNameInput ? 'rotate-180' : ''}`} />
                  </button>
                  {showNameInput && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder={defaultName || t('travelGuides.comments.enterNickname')}
                        maxLength={100}
                        className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {t('travelGuides.comments.nicknameHint')}
                      </p>
                    </div>
                  )}
                </div>

                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t('travelGuides.comments.placeholder')}
                  rows={3}
                  maxLength={2000}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                />

                {/* Notification opt-in */}
                <div className="mt-3 flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="notify-on-reply"
                    checked={notifyOnReply}
                    onChange={(e) => setNotifyOnReply(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="notify-on-reply" className="text-sm text-gray-600">
                    {t('travelGuides.comments.notifyOnReply')}
                  </label>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm text-gray-500">
                    {newComment.length}/2000
                  </span>
                  <button
                    type="submit"
                    disabled={!newComment.trim() || addComment.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="h-4 w-4" />
                    {addComment.isPending ? t('travelGuides.comments.submitting') : t('travelGuides.comments.submitButton')}
                  </button>
                </div>
              </div>
            </div>

            {addComment.isError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                {addComment.error.message}
              </div>
            )}

            {addComment.isSuccess && (
              <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded-lg">
                <CheckCircle className="h-4 w-4" />
                {t('travelGuides.comments.successMessage')}
              </div>
            )}
          </form>
        ) : (
          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <p className="text-gray-600 mb-4">{t('travelGuides.comments.signInPrompt')}</p>
            <div className="flex items-center justify-center gap-3">
              <SignInButton mode="modal">
                <button className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                  {t('travelGuides.comments.signInButton')}
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button className="inline-flex items-center gap-2 px-6 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors">
                  {t('travelGuides.comments.signUpButton')}
                </button>
              </SignUpButton>
            </div>
          </div>
        )}
      </div>

      {/* Comments List */}
      <div className="space-y-6">
        {isLoadingComments ? (
          <div className="text-center py-8 text-gray-500">{t('travelGuides.comments.loading')}</div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">{t('travelGuides.comments.noComments')}</div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="border-b border-gray-100 pb-6 last:border-0">
              <CommentItem
                comment={comment}
                locale={locale}
                onReply={isSignedIn ? handleReply : undefined}
                isReplying={replyingTo?.id === comment.id}
                onCancelReply={cancelReply}
                replyForm={
                  <form onSubmit={handleReplySubmit} className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                        {user?.imageUrl ? (
                          <Image
                            src={user.imageUrl}
                            alt=""
                            width={32}
                            height={32}
                            className="rounded-full object-cover"
                          />
                        ) : (
                          <User className="h-4 w-4 text-primary-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder={t('travelGuides.comments.replyPlaceholder')}
                          rows={2}
                          maxLength={2000}
                          autoFocus
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                        />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {replyText.length}/2000
                          </span>
                          <button
                            type="submit"
                            disabled={!replyText.trim() || addReply.isPending}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Send className="h-3 w-3" />
                            {addReply.isPending ? t('travelGuides.comments.submitting') : t('travelGuides.comments.replyButton')}
                          </button>
                        </div>
                      </div>
                    </div>
                    {addReply.isError && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        {addReply.error.message}
                      </div>
                    )}
                  </form>
                }
              />

              {/* Replies */}
              {comment.replies && comment.replies.length > 0 && (
                <div className="mt-4">
                  {/* Toggle button for replies */}
                  <button
                    onClick={() => toggleReplies(comment.id)}
                    className="ml-12 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    {expandedReplies.has(comment.id) ? (
                      <>
                        <ChevronUp className="h-4 w-4" />
                        {t('travelGuides.comments.hideReplies', { count: comment.replies.length })}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        {t('travelGuides.comments.showReplies', { count: comment.replies.length })}
                      </>
                    )}
                  </button>

                  {/* Expanded replies */}
                  {expandedReplies.has(comment.id) && (
                    <div className="space-y-4 mt-2">
                      {comment.replies.map((reply) => (
                        <CommentItem
                          key={reply.id}
                          comment={reply}
                          locale={locale}
                          isReply
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
